import { loadUserIfNeeded } from '../../actions/loadUserIfNeeded'
import { openError } from '../../actions/openError'
import {
    API_TIP,
    API_TIP_DEFAULTS,
    API_TIP_SET_DEFAULTS,
    SHARE_TIP_ENDPOINT,
} from '../../constants/endpoints'
import { URL_MFC_SHARE_TIP_MENUS } from '../../constants/urls'
import { APPSTATE, AppStateType, updateAppState } from '../../state/state.app'
import { USERSSTATE, slugToID } from '../../state/state.users'
import { controller } from '../../utilities/abortController'
import { toTokens } from '../toTokens'

export interface TipOptions {
    anonymousTip?: boolean
    chat_command?: 0 | 1
    chatError?: any
    comment?: string
    delayed?: boolean
    hide_amount?: 0 | 1
    publicComment?: boolean
    publicTip?: boolean
    repeating?: boolean
    silent?: 0 | 1
}

const TIP_FLAGS = ['a', 'd', 'h', 'm', 'p', 's', 'x'] as const

const TIP_PAYLOAD_FLAG_MAP: Record<(typeof TIP_FLAGS)[number], TipOptions> = {
    a: { anonymousTip: true },
    d: { delayed: true },
    h: { hide_amount: 1 },
    m: { publicComment: false },
    p: { publicTip: false },
    s: { silent: 1 },
    x: { repeating: true },
}

export const checkForRepeatingTip = (tipArguments = '') => {
    if (tipArguments && tipArguments.includes('x')) return true
    return false
}

export const checkForTipRepetitionDelay = (tipArguments = '') => {
    if (
        tipArguments &&
        tipArguments.includes('x') &&
        tipArguments.includes('d')
    ) {
        return true
    }
    return false
}

/** tip repetition delay in milliseconds */
export const getDelayValue = (tipArguments = ''): number => {
    const a = tipArguments.split('d')[1]
    return parseInt(a) * 1000
}

export const getRepetitionCount = (tipArguments: string): number => {
    return parseInt(tipArguments.split('d')[0].split('x')[1])
}

/** Provide an object with the appropriate tip values */
export const calculateTipArguments = (tipArguments = '') => {
    const resultArr: Array<Partial<TipOptions>> = []
    TIP_FLAGS.forEach((flag) => {
        if (!tipArguments.includes(flag)) return
        resultArr.push(TIP_PAYLOAD_FLAG_MAP[flag])
    })
    const result: TipOptions = Object.assign({}, ...resultArr)
    return result
}

export const makeShareTipMenuURL = (modelName: string, darkMode: boolean) =>
    `${URL_MFC_SHARE_TIP_MENUS}${modelName}?css=app&origin=app&color_scheme_dark=${darkMode}`

interface TipPayloadOptions {
    amount: number
    delay?: number
    error?: string
    payloadOptions: TipOptions
    repetitionCount?: number
}

export const buildTipPayloadOptions = (args: Array<string>) => {
    const tipFlags: Array<string> = []
    const argsWithoutTipFlags = args.slice(1).reduce((acc, arg) => {
        // if `arg` starts with `-` it is a tip flag
        const matches = /^-(\w+)$/.exec(arg)
        if (matches && matches[1]) {
            // args.length must be at least 3 to have tip arguments: ['/tip', '-tipFlags', 'amount']
            if (args.length >= 3) {
                tipFlags.push(matches[1])
            }
            // since `arg` is a tip flag, omit it from `argsWithoutTipFlags`
            return acc
        }
        // `arg` is not a tip flag, add it to `argsWithoutTipFlags`
        return [...acc, arg]
    }, [] as Array<string>)
    // safely grab tip amount if there is one
    const getAmount = (): [number?, string?] => {
        try {
            return [Number(argsWithoutTipFlags[0]), null]
        } catch (_e) {
            return [null, 'You must include a number for the token amount.']
        }
    }
    const tipArgString = tipFlags.join('')
    const payloadOptions = calculateTipArguments(tipArgString)
    // grab tip note/comment if it exists
    if (argsWithoutTipFlags.length >= 2) {
        payloadOptions.comment = argsWithoutTipFlags.splice(1).join(' ')
    }
    // display errors inline in the chat
    payloadOptions.chatError = true
    const [amount, error] = getAmount()
    const payload: TipPayloadOptions = { amount, error, payloadOptions }
    const isRepeatingTip = checkForRepeatingTip(tipArgString)
    const isRepetitionDelayed = isRepeatingTip
        ? checkForTipRepetitionDelay(tipArgString)
        : false
    if (isRepeatingTip) {
        payloadOptions.chat_command = 1
        payload.repetitionCount = getRepetitionCount(tipArgString)
        payload.delay = isRepetitionDelayed ? getDelayValue(tipArgString) : 1000
    }
    return payload
}

export async function sendTip(
    slugOrId: string,
    amount: number,
    options: TipOptions,
) {
    const model = await loadUserIfNeeded(slugOrId)
    if (!model) return

    const { name: myName } = USERSSTATE[slugToID['me']]
    const {
        session: { token },
    } = APPSTATE
    return postTip(amount, options, model.id, model.name, myName, token)
}

export async function tipViaDrawer(payload: {
    slug: string
    amount: number
    options: TipOptions
}) {
    if (!payload) throw new Error('Error sending tip')
    const { slug, amount, options } = payload
    if (!slug || !amount) throw new Error('Error sending tip')
    await sendTip(slug, amount, options)
    await setTipDefaults(options)
}

export async function sendTipRepeatedly(
    slugOrId: string | number,
    amount: number,
    tipOptions: TipOptions = {},
    repetitionCount: number,
    delay: number,
    callback: ({
        error,
        message,
    }: {
        error?: string
        message?: string
    }) => void,
) {
    const model = await loadUserIfNeeded(slugOrId)
    if (!model) return

    const delaySec = delay / 1000
    const tokens = toTokens(amount)
    const message =
        `Now sending ${repetitionCount} tips, ${tokens} each, ` +
        `spaced ${delaySec} ${delaySec === 1 ? 'second' : 'seconds'} apart. ` +
        `Disconnecting or reloading your browser will stop the automatic tipping.`
    callback({ message })

    const { chatError } = tipOptions
    const chat_command = 1 as const
    const options = { ...tipOptions, chat_command }
    const errorState = { message: null }
    const { name: myName } = USERSSTATE[slugToID['me']]
    const {
        session: { token },
    } = APPSTATE

    const _postTip = () =>
        postTip(amount, options, model.id, model.name, myName, token)
            .then(() => {
                if (chatError) {
                    const message = `You have tipped ${tokens}.`
                    callback({ message })
                }
            })
            .catch(({ message }) => {
                if (errorState.message === message) return
                errorState.message = message
                if (chatError) {
                    callback({ error: message })
                } else {
                    openError({ message })
                }
            })

    Array(repetitionCount)
        .fill(delay)
        .forEach((_, i) => setTimeout(_postTip, i > 0 ? i * delay : 1))
}

export async function sendShareTip(
    slugOrId: string | number,
    amount: number,
    target: string,
    message = '',
    options: TipOptions = {},
) {
    const model = await loadUserIfNeeded(slugOrId)
    if (!model) return
    const { id: model_id } = model
    const {
        session: { token },
    } = APPSTATE
    const { id: sender_id } = USERSSTATE[slugToID['me']]
    return postShareTip(
        amount,
        target,
        model_id,
        sender_id,
        token,
        message,
        options,
    )
}

type TipPayload = {
    anonymous: number // both
    chat_commands?: number // both, conditionally
    comment: string // both
    hide_amount: number //both
    my_username?: string // mobile and www do not use this
    public: number // both
    public_comment: number // both
    recipient_id: number // both
    recipient_username: string // mobile and www do not use this
    silent: number // both
    tip_amount: number // both
    token: string // both
}

async function postTip(
    amount: number,
    tipOptions: TipOptions = {},
    recipient_id: number,
    recipient_username: string,
    my_username: string,
    token: string,
) {
    const {
        anonymousTip = false,
        comment = '',
        chat_command = 0,
        hide_amount = 0,
        publicComment = !anonymousTip,
        publicTip = true,
        silent = 0,
    } = tipOptions
    const payload: TipPayload = {
        public_comment: publicComment ? 1 : 0,
        anonymous: anonymousTip ? 1 : 0,
        recipient_username,
        public: publicTip ? 1 : 0,
        tip_amount: amount,
        recipient_id,
        ...(!anonymousTip ? { my_username } : {}),
        ...(chat_command !== undefined ? { chat_command } : {}),
        hide_amount,
        comment,
        silent,
        token,
    }
    const resp = await fetch(API_TIP, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
    })
    const data = await resp.json()
    const { errmsg, err } = data
    if (err !== 0) throw new Error(errmsg)
    return data
}

interface PostShareTipResponse {
    code?: string
    error?: string
    message?: string
}

async function postShareTip(
    amount: number,
    target: string,
    model_id: number,
    sender_id: number,
    token: string,
    tip_message = '',
    options: any = {},
) {
    const payload = {
        amount,
        message: tip_message,
        model_id,
        options,
        target,
        token,
        user_id: sender_id,
    }
    const resp = await fetch(SHARE_TIP_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
    })
    const data = (await resp.json()) as PostShareTipResponse
    const { code, error, message } = data
    if (resp.status !== 200) {
        throw new Error(error || message)
    }
    if (code !== 'ok') throw new Error(message)
    return message
}

interface TipDefaults {
    anonymous: 0 | 1
    hide_amount: 0 | 1
    public: 0 | 1
    public_comment: 0 | 1
    silent: 0 | 1
    tip_amount: number
}

interface TipDefaultsResponse {
    result: TipDefaults
    err: number
}

async function fetchTipDefaults() {
    const resp = await fetch(API_TIP_DEFAULTS, {
        credentials: 'include',
        signal: controller.signal,
    })
    const data = (await resp.json()) as TipDefaultsResponse
    const { err, result } = data
    if (err !== 0) throw new Error('Error fetching tip defaults')
    return result
}

export async function storeTipDefaults() {
    const resp = await fetchTipDefaults()
    const tipDefaults = {
        ...(resp?.anonymous ? { anonymousTip: resp.anonymous === 1 } : {}),
        ...(resp?.public_comment
            ? { publicComment: resp.public_comment === 1 }
            : {}),
        ...(resp?.public ? { publicTip: resp.public === 1 } : {}),
    }
    updateAppState(AppStateType.TIP_DEFAULTS, tipDefaults)
}

type SetTipDefaultsPayload = Omit<TipDefaults, 'tip_amount'> & { token: string }

async function postSetTipDefaults(options: TipOptions = {}, token: string) {
    const { publicTip, anonymousTip, publicComment, hide_amount, silent } =
        options
    const payload: SetTipDefaultsPayload = {
        public: publicTip ? 1 : 0,
        anonymous: anonymousTip ? 1 : 0,
        public_comment: publicComment ? 1 : 0,
        hide_amount: hide_amount ? 1 : 0,
        silent: silent ? 1 : 0,
        token,
    }
    const resp = await fetch(API_TIP_SET_DEFAULTS, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
    })
    const data = (await resp.json()) as TipDefaultsResponse
    const { err, result } = data
    if (err !== 0) throw new Error('Error setting tip defaults')
    return result
}

export async function setTipDefaults(options: TipOptions) {
    const {
        session: { token },
        tipDefaults,
    } = APPSTATE
    if (
        options.anonymousTip === tipDefaults.anonymousTip &&
        options.publicComment === tipDefaults.publicComment &&
        options.publicTip === tipDefaults.publicTip
    ) {
        return
    }
    updateAppState(AppStateType.TIP_DEFAULTS, {
        anonymousTip: options.anonymousTip,
        publicComment: options.publicComment,
        publicTip: options.publicTip,
    })
    return postSetTipDefaults(options, token)
}
